import { useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, Loader2, Rocket, XCircle } from "lucide-react";
import type { ProjectDeployment } from "@mycompbox/shared";
import { useProjectCloud, useProjectDeployments, useStartDeployment } from "@/hooks/queries/use-cloud";
import { Button } from "@/components/ui/button";

/** GitHub lists a new run a few seconds after it's started; a run older than this is the previous deploy. */
const SLACK_MS = 60_000;

function runSince(d: ProjectDeployment, at: number): boolean {
  return !!d.runUrl && !!d.startedAt && Date.parse(d.startedAt) >= at - SLACK_MS;
}

function label(d: ProjectDeployment, starting: boolean): string {
  if (starting) return "Starting…";
  switch (d.state) {
    case "none":
      return "Not deployed yet";
    case "queued":
      return "Waiting to start";
    case "in_progress":
      return "Deploying…";
    case "success":
      return d.commit ? `Live · ${d.commit}` : "Live";
    case "failure":
      return "Last deploy failed";
    case "cancelled":
      return "Last deploy was cancelled";
    case "torn_down":
      return "Torn down";
  }
}

/**
 * Deploy buttons for each of the project's environments, with each one's
 * latest deploy and live address.
 */
export function ProjectDeployments({ teamId, isAdmin }: { teamId: string; isAdmin: boolean }) {
  // When each environment's deploy was started here, until its run shows up.
  const [started, setStarted] = useState<Record<string, number>>({});
  const watching = Object.keys(started).length > 0;
  const { data: deployments, isLoading } = useProjectDeployments(teamId, true, watching);
  const start = useStartDeployment(teamId);
  const { data: project } = useProjectCloud(teamId);

  // Once a started deploy's run is listed, stop treating it as "starting".
  useEffect(() => {
    if (!deployments) return;
    const seen = deployments.filter((d) => {
      const at = started[d.environment];
      return at !== undefined && runSince(d, at);
    });
    if (seen.length === 0) return;
    setStarted((s) => Object.fromEntries(Object.entries(s).filter(([env]) => !seen.some((d) => d.environment === env))));
  }, [deployments, started]);

  if (isLoading || !deployments) return <div className="h-12 animate-pulse rounded-lg bg-muted" />;

  const isStarting = (d: ProjectDeployment) => {
    const at = started[d.environment];
    return at !== undefined && !runSince(d, at);
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">
        Deploys
        {project?.cloud ? (
          <span className="ml-2 font-normal text-muted-foreground">
            to {project.cloud.name}
            {project.isInherited ? " (default)" : ""}
          </span>
        ) : null}
      </p>
      <ul className="space-y-2">
        {deployments.map((d) => {
          const starting = isStarting(d);
          const running = starting || d.state === "queued" || d.state === "in_progress";
          const allowed = d.environment === "dev" || isAdmin;
          return (
            <li
              key={d.environment}
              className="flex flex-col gap-2 rounded-lg border border-white/[0.08] p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-center gap-2">
                {running ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
                ) : d.state === "success" ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                ) : d.state === "failure" ? (
                  <XCircle className="h-4 w-4 shrink-0 text-destructive" />
                ) : (
                  <Rocket className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className="font-medium capitalize">{d.environment}</span>
                <span className="text-xs text-muted-foreground">{label(d, starting)}</span>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {d.state === "success" && !running ? (
                  <Button asChild size="sm" variant="outline">
                    <a href={d.url} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1 h-3.5 w-3.5" />
                      Open app
                    </a>
                  </Button>
                ) : null}
                {d.runUrl ? (
                  <Button asChild size="sm" variant="ghost">
                    <a href={d.runUrl} target="_blank" rel="noreferrer">
                      Run log
                    </a>
                  </Button>
                ) : null}
                {allowed ? (
                  <Button
                    size="sm"
                    disabled={running || start.isPending}
                    onClick={() => {
                      const at = Date.now();
                      start.mutate(d.environment, {
                        onSuccess: () => { setStarted((s) => ({ ...s, [d.environment]: at })); },
                      });
                    }}
                  >
                    Deploy to {d.environment}
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {start.error ? <p className="text-sm text-destructive">{start.error.message}</p> : null}
      {deployments.some((d) => d.environment === "prod") ? (
        <p className="text-xs text-muted-foreground">Prod waits for approval in GitHub if its environment has required reviewers.</p>
      ) : null}
    </div>
  );
}
